import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";

const Stat = ({ label, value, testId }) => (
  <div className="bg-white border border-stone-200 rounded-lg p-5"><p className="text-xs uppercase tracking-[0.15em] text-stone-500">{label}</p><p className="font-heading text-3xl font-black text-stone-900 mt-1" data-testid={testId}>{value}</p></div>
);

export const UsagePanel = ({ keys }) => {
  const api = useApi();
  const [keyId, setKeyId] = useState("all");
  const [d, setD] = useState(null);
  const load = useCallback(() => api("get", `/api-keys/usage/stats?days=14${keyId !== "all" ? `&key_id=${keyId}` : ""}`).then(setD).catch((e) => toast.error(errMsg(e))), [api, keyId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6" data-testid="usage-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={keyId} onValueChange={setKeyId}>
          <SelectTrigger className="w-64 bg-white" data-testid="usage-key-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toutes les clés</SelectItem>{keys.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}</SelectContent>
        </Select>
        <button className="text-sm text-[#C2410C] hover:underline" onClick={load} data-testid="usage-refresh">Actualiser</button>
      </div>
      {!d ? <p className="text-sm text-stone-500">Chargement…</p> : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <Stat label="Appels (14 jours)" value={d.total} testId="usage-total" />
            <Stat label="Erreurs / refus" value={d.errors} testId="usage-errors" />
            <Stat label="Taux de succès" value={d.total ? `${Math.round(((d.total - d.errors) / d.total) * 100)} %` : "—"} testId="usage-success" />
          </div>
          <div className="bg-white border border-stone-200 rounded-lg p-6">
            <h3 className="font-heading text-base font-bold text-stone-900 mb-4">Appels par jour</h3>
            <div className="h-56"><ResponsiveContainer><BarChart data={d.by_day.map((x) => ({ ...x, ok: x.count - x.errors, day: x.day.slice(5) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" /><XAxis dataKey="day" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip />
              <Bar dataKey="ok" name="Réussis" stackId="a" fill="#C2410C" /><Bar dataKey="errors" name="Erreurs" stackId="a" fill="#a8a29e" />
            </BarChart></ResponsiveContainer></div>
          </div>
          <CallLog log={d.log} />
        </>
      )}
    </div>
  );
};

const CallLog = ({ log }) => (
  <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="usage-log">
    <h3 className="font-heading text-base font-bold text-stone-900">Derniers appels</h3>
    <div className="mt-3 divide-y divide-stone-100 text-sm overflow-x-auto">
      {log.length === 0 && <p className="py-2 text-stone-500">Aucun appel enregistré.</p>}
      {log.map((c, i) => (
        <div key={i} className="py-2 flex gap-4 whitespace-nowrap">
          <span className={`font-mono text-xs w-10 ${c.status >= 400 ? "text-red-700" : "text-green-700"}`}>{c.status}</span>
          <span className="font-mono text-xs w-14 text-stone-500">{c.method}</span>
          <span className="font-mono text-xs">{c.path}</span>
          <span className="text-stone-600">{c.key_name}</span>
          <span className="text-stone-500">{c.ip}</span>
          <span className="text-stone-500">{c.ms} ms</span>
          <span className="ml-auto text-stone-500">{new Date(c.at).toLocaleString("fr-FR")}</span>
        </div>
      ))}
    </div>
  </div>
);
